import {useState, type ReactNode} from 'react'
import {Button, Modal, Badge, Spinner, Table, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {GET_ONE_PDF_ENVELOPE} from '../../../gql/queries/pdfEnvelopesApiQueries'
import {RESEND_PDF_ENVELOPE_SIGNER, VOID_PDF_ENVELOPE} from '../../../gql/mutations/pdfEnvelopesMutations'
import {NodePdfEnvelope, NodePdfEnvelopeSigner} from '../../../types/pdfEnvelopesApi'
import {BadgeStatus} from './BadgeStatus'
import {parseId} from '../../../helpers'
import {STORAGE_NAMES} from '../../../helpers/consts'
import moment from 'moment'
import {URL as BACKEND_URL} from '../../../gql/client'

interface Props {
  envelopeId: string
  isOpen: boolean
  onClose: () => void
  refetch?: () => void
}

interface ResendState {
  signer: NodePdfEnvelopeSigner
  name: string
  email: string
  phone: string
  sendEmail: boolean
  sendSms: boolean
  getLink: boolean
  signingUrl: string | null
  done: boolean
}

const emDash = '—'
const hashMark = '#'
const boldChunk = (chunks: ReactNode) => <strong>{chunks}</strong>

export const ModalEnvelopePreview = ({envelopeId, isOpen, onClose, refetch}: Props) => {
  const intl = useIntl()
  const [downloading, setDownloading] = useState(false)
  const [showVoidConfirm, setShowVoidConfirm] = useState(false)
  const [resendState, setResendState] = useState<ResendState | null>(null)

  const {data, loading, refetch: refetchEnvelope} = useQuery(GET_ONE_PDF_ENVELOPE, {
    variables: {id: parseId(envelopeId)},
    skip: !envelopeId,
    fetchPolicy: 'no-cache',
  })

  const [voidEnvelope, {loading: voiding}] = useMutation(VOID_PDF_ENVELOPE)
  const [resendSigner, {loading: resending}] = useMutation(RESEND_PDF_ENVELOPE_SIGNER)

  const envelope: NodePdfEnvelope | null = data?.pdfEnvelopeById ?? null

  const pendingSigners = envelope?.signers.filter(
    (s) => s.status === 'PENDING' || s.status === 'VIEWED'
  ) ?? []
  const signedSigners = envelope?.signers.filter((s) => s.status === 'SIGNED') ?? []
  const envTitle =
    envelope?.title ??
    intl.formatMessage({
      id: 'documents.pdfEnvelopes.envelope.titleFallback',
      defaultMessage: 'Envelope',
    })

  const handleVoidConfirmed = async () => {
    try {
      const {data} = await voidEnvelope({variables: {envelopeId: parseId(envelopeId)}})
      if (data?.pdfEnvelopeVoid?.ok) {
        toast.success(
          intl.formatMessage({
            id: 'documents.pdfEnvelopes.envelope.toast.voided',
            defaultMessage: 'Envelope voided',
          })
        )
        setShowVoidConfirm(false)
        refetchEnvelope()
        refetch?.()
      } else {
        toast.error(
          data?.pdfEnvelopeVoid?.errorMessage ||
            intl.formatMessage({
              id: 'documents.pdfEnvelopes.envelope.toast.voidError',
              defaultMessage: 'Error voiding',
            })
        )
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const openResend = (signer: NodePdfEnvelopeSigner) => {
    setResendState({
      signer,
      name: signer.recipientName,
      email: signer.recipientEmail ?? '',
      phone: signer.recipientPhone ?? '',
      sendEmail: true,
      sendSms: false,
      getLink: false,
      signingUrl: null,
      done: false,
    })
  }

  const handleResendSubmit = async () => {
    if (!resendState) return
    if (!resendState.sendEmail && !resendState.sendSms && !resendState.getLink) {
      toast.error(
        intl.formatMessage({
          id: 'documents.pdfEnvelopes.request.toast.selectChannel',
          defaultMessage: 'Select at least one delivery channel',
        })
      )
      return
    }
    try {
      const {data} = await resendSigner({
        variables: {
          signerId: parseId(resendState.signer.id),
          sendEmail: resendState.sendEmail,
          sendSms: resendState.sendSms,
          getSigningLink: resendState.getLink,
          recipientName: resendState.name || undefined,
          recipientEmail: resendState.email || undefined,
          recipientPhone: resendState.phone || undefined,
        },
      })
      if (data?.pdfEnvelopeSignerResend?.ok) {
        const url = data.pdfEnvelopeSignerResend.signingUrl ?? null
        setResendState((prev) => prev ? {...prev, signingUrl: url, done: true} : prev)
        if (!url)
          toast.success(
            intl.formatMessage(
              {id: 'documents.pdfEnvelopes.envelope.toast.resentTo', defaultMessage: 'Resent to {name}'},
              {name: resendState.name || resendState.signer.recipientName}
            )
          )
        refetchEnvelope()
      } else {
        toast.error(
          data?.pdfEnvelopeSignerResend?.errorMessage ||
            intl.formatMessage({
              id: 'documents.pdfEnvelopes.envelope.toast.resendError',
              defaultMessage: 'Error resending',
            })
        )
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const [downloadingExternal, setDownloadingExternal] = useState(false)

  const _fetchAndDownload = async (path: string, filename: string) => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    const url = `${BACKEND_URL}${path}`
    const res = await fetch(url, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
    if (!res.ok) throw new Error(`Error ${res.status}`)
    const blob = await res.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    link.click()
    window.URL.revokeObjectURL(objectUrl)
  }

  const handleDownload = async () => {
    if (!envelope) return
    setDownloading(true)
    try {
      await _fetchAndDownload(
        `/pdf-envelopes/${parseId(envelope.id)}/download/`,
        `${envelope.title.replace(/\s+/g, '_')}.pdf`
      )
    } catch (e: any) {
      toast.error(
        intl.formatMessage(
          {id: 'documents.pdfEnvelopes.envelope.toast.downloadError', defaultMessage: 'Error downloading: {message}'},
          {message: e.message}
        )
      )
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadExternal = async () => {
    if (!envelope) return
    setDownloadingExternal(true)
    try {
      await _fetchAndDownload(
        `/pdf-envelopes/${parseId(envelope.id)}/download-external/`,
        `${envelope.title.replace(/\s+/g, '_')}_external.pdf`
      )
    } catch (e: any) {
      toast.error(
        intl.formatMessage(
          {id: 'documents.pdfEnvelopes.envelope.toast.downloadError', defaultMessage: 'Error downloading: {message}'},
          {message: e.message}
        )
      )
    } finally {
      setDownloadingExternal(false)
    }
  }

  return (
    <>
      <Modal show={isOpen} onHide={onClose} size='xl' backdrop='static'>
        <Modal.Header closeButton>
          <Modal.Title>
            {envTitle} {envelope && <BadgeStatus state={envelope.status} />}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' />
            </div>
          )}
          {envelope && (
            <>
              {/* Signers status */}
              <h6 className='fw-bold mb-2'>
                <FormattedMessage id='documents.pdfEnvelopes.request.signers' defaultMessage='Signers' />
              </h6>
              <Table bordered size='sm' className='mb-4'>
                <thead>
                  <tr>
                    <th>{hashMark}</th>
                    <th>
                      <FormattedMessage id='common.field.name' defaultMessage='Name' />
                    </th>
                    <th>
                      <FormattedMessage id='documents.pdfEnvelopes.envelope.col.role' defaultMessage='Role' />
                    </th>
                    <th>
                      <FormattedMessage id='common.field.email' defaultMessage='Email' />
                    </th>
                    <th>
                      <FormattedMessage id='common.field.status' defaultMessage='Status' />
                    </th>
                    <th>
                      <FormattedMessage id='common.status.signed' defaultMessage='Signed' />
                    </th>
                    <th>
                      <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {envelope.signers.map((s, i) => (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td>{s.recipientName}</td>
                      <td>
                        <Badge bg='secondary'>{s.role}</Badge>
                      </td>
                      <td>{s.recipientEmail ?? emDash}</td>
                      <td>
                        <BadgeStatus state={s.status} />
                      </td>
                      <td>{s.signedOn ? moment(s.signedOn).format('DD MMM YY HH:mm') : emDash}</td>
                      <td>
                        {s.status !== 'SIGNED' && s.status !== 'VOIDED' && (
                          <Button
                            size='sm'
                            variant='outline-primary'
                            onClick={() => openResend(s)}
                          >
                            <i className='bi bi-send' /> <FormattedMessage
                              id='common.action.resend'
                              defaultMessage='Resend'
                            />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Activity log */}
              {envelope.logs && envelope.logs.length > 0 && (
                <>
                  <h6 className='fw-bold mb-2'>
                    <FormattedMessage
                      id='documents.pdfEnvelopes.envelope.activityLog'
                      defaultMessage='Activity log'
                    />
                  </h6>
                  <Table bordered size='sm' className='mb-3' style={{fontSize: 12}}>
                    <thead>
                      <tr>
                        <th>
                          <FormattedMessage id='documents.pdfEnvelopes.envelope.log.date' defaultMessage='Date' />
                        </th>
                        <th>
                          <FormattedMessage id='common.field.action' defaultMessage='Action' />
                        </th>
                        <th>
                          <FormattedMessage id='common.field.contact' defaultMessage='Contact' />
                        </th>
                        <th>
                          <FormattedMessage id='common.field.ip' defaultMessage='IP' />
                        </th>
                        <th>
                          <FormattedMessage id='documents.pdfEnvelopes.envelope.log.location' defaultMessage='Location' />
                        </th>
                        <th>
                          <FormattedMessage id='documents.pdfEnvelopes.envelope.log.browser' defaultMessage='Browser' />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {envelope.logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.created ? moment(log.created).format('DD MMM YY HH:mm') : emDash}</td>
                          <td>
                            <Badge bg='light' text='dark'>
                              {log.action}
                            </Badge>
                          </td>
                          <td>{log.contact ?? emDash}</td>
                          <td style={{whiteSpace: 'nowrap'}}>{log.ipAddress ?? emDash}</td>
                          <td style={{whiteSpace: 'nowrap'}}>{log.region ?? emDash}</td>
                          <td>{log.browser ? log.browser.substring(0, 40) : emDash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {envelope?.status === 'COMPLETED' && (
            <>
              <Button variant='success' onClick={handleDownload} disabled={downloading}>
                {downloading ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  <>
                    <i className='bi bi-file-earmark-pdf' /> <FormattedMessage
                      id='documents.pdfEnvelopes.envelope.downloadFull'
                      defaultMessage='Download (full log)'
                    />
                  </>
                )}
              </Button>
              <Button variant='outline-success' onClick={handleDownloadExternal} disabled={downloadingExternal}>
                {downloadingExternal ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  <>
                    <i className='bi bi-file-earmark-arrow-down' /> <FormattedMessage
                      id='documents.pdfEnvelopes.envelope.downloadClient'
                      defaultMessage='Download (client view)'
                    />
                  </>
                )}
              </Button>
            </>
          )}
          {envelope && !['VOIDED', 'EXPIRED', 'COMPLETED'].includes(envelope.status) && (
            <Button variant='danger' onClick={() => setShowVoidConfirm(true)} disabled={voiding}>
              <FormattedMessage
                id='documents.pdfEnvelopes.envelope.void'
                defaultMessage='Void envelope'
              />
            </Button>
          )}
          <Button variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Resend modal */}
      {resendState && (
        <Modal
          show={!!resendState}
          onHide={() => setResendState(null)}
          centered
          backdrop='static'
          style={{zIndex: 1060}}
          size='sm'
        >
          <Modal.Header closeButton>
            <Modal.Title style={{fontSize: 15}}>
              <i className='bi bi-send me-2' />
              {intl.formatMessage(
                {id: 'documents.pdfEnvelopes.envelope.resendTitle', defaultMessage: 'Resend — {role}'},
                {role: resendState.signer.role}
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {resendState.done && resendState.signingUrl ? (
              <>
                <div className='alert alert-success py-2' style={{fontSize: 13}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.envelope.resentSuccess'
                    defaultMessage='Resent successfully.'
                  />
                </div>
                <p className='fw-bold mb-1' style={{fontSize: 13}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.envelope.signingLink'
                    defaultMessage='Signing link:'
                  />
                </p>
                <div className='p-2 border rounded mb-2' style={{background: '#f8f9fa', wordBreak: 'break-all', fontSize: 12}}>
                  <a href={resendState.signingUrl} target='_blank' rel='noreferrer'>
                    {resendState.signingUrl}
                  </a>
                </div>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  className='w-100'
                  onClick={() => {
                    navigator.clipboard.writeText(resendState.signingUrl!)
                    toast.info(
                      intl.formatMessage({
                        id: 'documents.pdfEnvelopes.envelope.toast.linkCopied',
                        defaultMessage: 'Link copied',
                      })
                    )
                  }}
                >
                  <i className='bi bi-clipboard me-1' /> <FormattedMessage
                    id='documents.pdfEnvelopes.envelope.copyLink'
                    defaultMessage='Copy link'
                  />
                </Button>
              </>
            ) : resendState.done ? (
              <div className='alert alert-success py-2' style={{fontSize: 13}}>
                <FormattedMessage
                  id='documents.pdfEnvelopes.envelope.resentSuccess'
                  defaultMessage='Resent successfully.'
                />
              </div>
            ) : (
              <>
                <Form.Group className='mb-2'>
                  <Form.Label style={{fontSize: 12}}>
                    <FormattedMessage id='common.field.name' defaultMessage='Name' />
                  </Form.Label>
                  <Form.Control
                    size='sm'
                    value={resendState.name}
                    onChange={(e) => setResendState((p) => p ? {...p, name: e.target.value} : p)}
                  />
                </Form.Group>
                <Form.Group className='mb-2'>
                  <Form.Label style={{fontSize: 12}}>
                    <FormattedMessage id='common.field.email' defaultMessage='Email' />
                  </Form.Label>
                  <Form.Control
                    size='sm'
                    type='email'
                    value={resendState.email}
                    onChange={(e) => setResendState((p) => p ? {...p, email: e.target.value} : p)}
                  />
                </Form.Group>
                <Form.Group className='mb-3'>
                  <Form.Label style={{fontSize: 12}}>
                    <FormattedMessage id='common.field.phone' defaultMessage='Phone' />
                  </Form.Label>
                  <Form.Control
                    size='sm'
                    value={resendState.phone}
                    onChange={(e) => setResendState((p) => p ? {...p, phone: e.target.value} : p)}
                  />
                </Form.Group>
                <p className='fw-bold mb-1' style={{fontSize: 12}}>
                  <FormattedMessage
                    id='documents.pdfEnvelopes.request.deliveryChannels'
                    defaultMessage='Delivery channels'
                  />
                </p>
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({id: 'common.field.email', defaultMessage: 'Email'})}
                  checked={resendState.sendEmail}
                  onChange={(e) => setResendState((p) => p ? {...p, sendEmail: e.target.checked} : p)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({id: 'documents.pdfEnvelopes.request.channel.sms', defaultMessage: 'SMS'})}
                  checked={resendState.sendSms}
                  onChange={(e) => setResendState((p) => p ? {...p, sendSms: e.target.checked} : p)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label={intl.formatMessage({id: 'documents.pdfEnvelopes.request.channel.getLink', defaultMessage: 'Get signing link'})}
                  checked={resendState.getLink}
                  onChange={(e) => setResendState((p) => p ? {...p, getLink: e.target.checked} : p)}
                  style={{fontSize: 13}}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer className='py-2'>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setResendState(null)}
            >
              {resendState.done ? (
                <FormattedMessage id='common.action.close' defaultMessage='Close' />
              ) : (
                <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
              )}
            </Button>
            {!resendState.done && (
              <Button size='sm' onClick={handleResendSubmit} disabled={resending}>
                {resending ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  <>
                    <i className='bi bi-send me-1' />
                    <FormattedMessage id='common.action.send' defaultMessage='Send' />
                  </>
                )}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {/* Void confirmation modal */}
      <Modal
        show={showVoidConfirm}
        onHide={() => setShowVoidConfirm(false)}
        centered
        backdrop='static'
        style={{zIndex: 1060}}
      >
        <Modal.Header className='bg-danger text-white'>
          <Modal.Title style={{fontSize: 16}}>
            <i className='bi bi-exclamation-triangle-fill me-2' />
            <FormattedMessage
              id='documents.pdfEnvelopes.envelope.void.title'
              defaultMessage='Void envelope — are you sure?'
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{fontSize: 14}}>
            <FormattedMessage
              id='documents.pdfEnvelopes.envelope.void.intro'
              defaultMessage='You are about to <b>permanently cancel</b> this envelope. This action <b>cannot be undone</b>. The following will happen immediately:'
              values={{b: boldChunk}}
            />
          </p>
          <ul style={{fontSize: 14, marginBottom: 12}}>
            <li>
              <FormattedMessage
                id='documents.pdfEnvelopes.envelope.void.item1'
                defaultMessage='The envelope will be marked as <b>VOIDED</b> and no further signatures will be accepted.'
                values={{b: boldChunk}}
              />
            </li>
            {pendingSigners.length > 0 && (
              <li>
                <FormattedMessage
                  id='documents.pdfEnvelopes.envelope.void.item2'
                  defaultMessage='The following signers <b>will lose access</b> to their signing link — their link will show as invalid if they try to open it:'
                  values={{b: boldChunk}}
                />
                <ul className='mt-1'>
                  {pendingSigners.map((s) => (
                    <li key={s.id}>
                      <FormattedMessage
                        id='documents.pdfEnvelopes.envelope.void.signerLine'
                        defaultMessage='<b>{name}</b>{emailSuffix} — currently <b>{status}</b>'
                        values={{
                          name: s.recipientName,
                          emailSuffix: s.recipientEmail ? ` (${s.recipientEmail})` : '',
                          status: s.status,
                          b: boldChunk,
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            )}
            {signedSigners.length > 0 && (
              <li>
                <FormattedMessage
                  id='documents.pdfEnvelopes.envelope.void.item3'
                  defaultMessage='{count, plural, one {The # signer who already signed} other {The # signers who already signed}} ({names}) will remain in the activity log, but the document will <b>not</b> be considered fully executed.'
                  values={{
                    count: signedSigners.length,
                    names: signedSigners.map((s) => s.recipientName).join(', '),
                    b: boldChunk,
                  }}
                />
              </li>
            )}
            <li>
              <FormattedMessage
                id='documents.pdfEnvelopes.envelope.void.item4'
                defaultMessage='The activity log and all recorded events will be preserved for reference.'
              />
            </li>
          </ul>
          <div
            className='alert alert-warning d-flex align-items-start gap-2 mb-0'
            style={{fontSize: 13}}
          >
            <i className='bi bi-info-circle-fill mt-1' />
            <span>
              <FormattedMessage
                id='documents.pdfEnvelopes.envelope.void.warning'
                defaultMessage='If you need the document signed, you will have to send a <b>new envelope</b> from the template.'
                values={{b: boldChunk}}
              />
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowVoidConfirm(false)} disabled={voiding}>
            <FormattedMessage
              id='documents.pdfEnvelopes.envelope.void.cancel'
              defaultMessage='Cancel — keep the envelope active'
            />
          </Button>
          <Button variant='danger' onClick={handleVoidConfirmed} disabled={voiding}>
            {voiding ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <>
                <i className='bi bi-x-circle me-1' />
                <FormattedMessage
                  id='documents.pdfEnvelopes.envelope.void.confirm'
                  defaultMessage='Yes, void this envelope'
                />
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
